<script setup lang="ts">
import { ref, computed } from 'vue';
import { useConsentStore } from '@/stores/consent';
import { CheckIcon, XMarkIcon, Cog6ToothIcon } from '@heroicons/vue/24/outline';

/**
 * Cookie-/Consent-Banner. Erscheint nur, wenn noch keine Entscheidung getroffen
 * wurde, oder wenn der User via Footer „Cookie-Einstellungen" anklickt.
 *
 * Drei Aktionen vom Banner:
 *   1. Alle akzeptieren        — bequem, fuer Nicht-Pingelige
 *   2. Nur erforderliche       — strenge Variante, alles ausser essential aus
 *   3. Anpassen                — Drawer mit Toggles pro Kategorie
 *
 * Wichtig: YouTube-, Discord-Widget- und aehnliche Embeds duerfen nicht
 * geladen werden, solange `consent.allowMarketing` falsch ist. Das wird von
 * den jeweiligen Components in den Views geprueft, nicht hier.
 */
const consent = useConsentStore();
const open = computed(() => !consent.decided);
const detailsOpen = ref(false);

// Lokale Kopie fuer das Anpassen-Panel — wird erst beim Save uebernommen
const draft = ref({
  preferences: consent.state.preferences,
  statistics:  consent.state.statistics,
  marketing:   consent.state.marketing
});

function acceptAll() { consent.acceptAll(); detailsOpen.value = false; }
function rejectAll() { consent.rejectAll(); detailsOpen.value = false; }
function saveDraft() {
  consent.save(draft.value);
  detailsOpen.value = false;
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6 animate-slide-in"
      role="dialog"
      aria-label="Cookie-Einstellungen"
      aria-modal="false"
    >
      <div class="max-w-3xl mx-auto bg-slate-900/95 backdrop-blur-xl
                  border border-slate-700 rounded-2xl shadow-2xl shadow-black/50">

        <!-- Kompaktansicht -->
        <div v-if="!detailsOpen" class="p-6 sm:p-8">
          <div class="flex items-start gap-4 mb-5">
            <div class="hidden sm:flex h-10 w-10 rounded-lg bg-brand-500/15 ring-1 ring-brand-500/30
                        items-center justify-center text-brand-300 flex-shrink-0">
              🍪
            </div>
            <div>
              <h2 class="font-bold text-white mb-1">Wir respektieren deine Privatsphäre</h2>
              <p class="text-sm text-slate-400 leading-relaxed">
                Wir verwenden technisch erforderliche Cookies, damit du dich einloggen kannst.
                Für YouTube-Videos, eingebettete Discord-Widgets und ähnliche Drittinhalte
                brauchen wir deine Zustimmung — dabei werden Daten an externe Anbieter übertragen.
                Du kannst deine Auswahl jederzeit im Footer ändern.
                <RouterLink to="/datenschutz" class="link">Mehr in der Datenschutzerklärung.</RouterLink>
              </p>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row gap-2">
            <button @click="rejectAll" class="btn-secondary flex-1 justify-center">
              <XMarkIcon class="w-4 h-4" /> Nur erforderliche
            </button>
            <button @click="detailsOpen = true" class="btn-ghost flex-1 justify-center">
              <Cog6ToothIcon class="w-4 h-4" /> Anpassen
            </button>
            <button @click="acceptAll" class="btn-primary flex-1 justify-center">
              <CheckIcon class="w-4 h-4" /> Alle akzeptieren
            </button>
          </div>
        </div>

        <!-- Anpassen-Panel -->
        <div v-else class="p-6 sm:p-8">
          <div class="flex items-center justify-between mb-5">
            <h2 class="font-bold text-white">Cookie-Einstellungen anpassen</h2>
            <button @click="detailsOpen = false" class="text-slate-400 hover:text-white"
                    aria-label="Schließen">
              <XMarkIcon class="w-5 h-5" />
            </button>
          </div>

          <ul class="space-y-4 mb-6">
            <li class="flex items-start justify-between gap-4">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-sm font-semibold text-white">Technisch erforderlich</span>
                  <span class="badge-brand">Immer aktiv</span>
                </div>
                <p class="text-xs text-slate-400">
                  Session-Cookie für den Login, Speicherung der Cookie-Auswahl selbst.
                  Ohne diese funktioniert das Dashboard nicht. Keine Übertragung an Dritte.
                </p>
              </div>
              <input type="checkbox" checked disabled class="mt-1 accent-brand-500" />
            </li>

            <li class="flex items-start justify-between gap-4">
              <div class="flex-1">
                <div class="text-sm font-semibold text-white mb-1">Präferenzen</div>
                <p class="text-xs text-slate-400">
                  Speichert UI-Einstellungen wie Sprache oder Layout. Bleibt lokal in deinem
                  Browser.
                </p>
              </div>
              <input type="checkbox" v-model="draft.preferences"
                     class="mt-1 accent-brand-500" />
            </li>

            <li class="flex items-start justify-between gap-4">
              <div class="flex-1">
                <div class="text-sm font-semibold text-white mb-1">Statistiken</div>
                <p class="text-xs text-slate-400">
                  Anonyme Nutzungsstatistiken, damit wir wissen, welche Bereiche besucht werden.
                  Aktuell nicht aktiv — vorgesehen für späteres self-hosted Analytics.
                </p>
              </div>
              <input type="checkbox" v-model="draft.statistics"
                     class="mt-1 accent-brand-500" />
            </li>

            <li class="flex items-start justify-between gap-4">
              <div class="flex-1">
                <div class="text-sm font-semibold text-white mb-1">Externe Inhalte (Marketing)</div>
                <p class="text-xs text-slate-400">
                  Notwendig für eingebettete <strong>YouTube</strong>-Videos und das
                  <strong>Discord</strong>-Widget. Diese Anbieter setzen eigene Cookies und
                  erhalten deine IP-Adresse. Ohne deine Zustimmung zeigen wir nur einen
                  Platzhalter mit Link.
                </p>
              </div>
              <input type="checkbox" v-model="draft.marketing"
                     class="mt-1 accent-brand-500" />
            </li>
          </ul>

          <div class="flex flex-col sm:flex-row gap-2 pt-4 border-t border-slate-800">
            <button @click="rejectAll" class="btn-secondary flex-1 justify-center">Alles ablehnen</button>
            <button @click="saveDraft" class="btn-primary flex-1 justify-center">Auswahl speichern</button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
